import { MouseEventHandler, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import LoadingSpinner from '@/components/LoadingSpinner'
import { cn } from '@/lib/misc/Utils'

export type ButtonStyle =
  | 'primary'
  | 'secondary'
  | 'normal'
  | 'success'
  | 'danger'
  | 'light'
  | 'flat'

export type ButtonSize = 'sm' | 'md' | 'lg'

const buttonSizesMap: Record<ButtonSize, string> = {
  sm: 'h-8 px-4 gap-1 text-xs',
  md: 'h-10 px-4 gap-2 text-sm',
  lg: 'h-[52px] px-6 gap-2 text-base',
}

const spinnerSizesMap: Record<ButtonSize, string> = {
  sm: 'size-3',
  md: 'size-4',
  lg: 'size-5',
}

const buttonStylesMap: Record<ButtonStyle, string> = {
  primary:
    'bg-accent-60 border-accent-60 hover:bg-white text-white hover:text-accent-60',
  secondary: 'border-accent-60 text-accent-60 hover:opacity-50 duration-200',
  normal:
    'bg-white border-accent-60 text-accent-60 hover:bg-accent-60 hover:text-white disabled:bg-white disabled:text-accent-60',
  success:
    'bg-status-good border-status-good hover:bg-status-good/70 hover:border-status-good/70 text-white',
  danger:
    'bg-red-500 border-red-500 hover:bg-red-400 hover:border-red-400 text-white',
  light: 'bg-accent-20 hover:bg-accent-30 border-0 text-brand-4',
  flat: 'bg-white hover:bg-neutral-10 border-0 text-brand-4',
}

interface ButtonProps {
  className?: string
  icon?: ReactNode
  iconSuffix?: ReactNode
  label?: string
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined
  href?: string
  type?: 'button' | 'submit'
  children?: ReactNode
  disabled?: boolean
  loading?: boolean
  style?: ButtonStyle
  size?: ButtonSize
  fullWidth?: boolean
  id?: string
  cy?: string
  as?: 'button' | 'link' | 'anchor'
  target?: string
}

const Button = ({
  children,
  icon,
  iconSuffix,
  disabled,
  loading,
  className,
  onClick,
  style,
  size = 'md',
  fullWidth,
  type = 'button',
  href,
  id,
  cy,
  as = 'button',
  target,
}: ButtonProps) => {
  const isDisabled = disabled || loading
  const displayIcon = loading ? (
    <LoadingSpinner className={spinnerSizesMap[size]} interlinkLogo={false} />
  ) : (
    icon
  )

  const buttonStyles = cn(
    'flex items-center border font-medium rounded-md select-none transition-all duration-300 transform',
    'outline-none focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'motion-safe:active:scale-[0.95]',
    buttonSizesMap[size],
    style ? buttonStylesMap[style] : 'border-transparent',
    isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    fullWidth && 'w-full justify-center',
    className || ''
  )

  const renderContent = () => (
    <>
      {!!displayIcon && <div className="flex items-center">{displayIcon}</div>}
      {!!children && <div className="leading-tighter">{children}</div>}
      {!!iconSuffix && <div className="flex items-center">{iconSuffix}</div>}
    </>
  )

  if (as === 'link' && href) {
    return (
      <Link
        to={href}
        className={buttonStyles}
        id={id}
        data-cy={cy}
        target={target}
        onClick={(e: any) => (onClick ? onClick(e) : undefined)}
      >
        {renderContent()}
      </Link>
    )
  }

  if (as === 'anchor' && href) {
    return (
      <a
        href={href}
        className={buttonStyles}
        id={id}
        data-cy={cy}
        target={target}
      >
        {renderContent()}
      </a>
    )
  }

  return (
    <button
      onClick={onClick}
      type={type}
      id={id}
      data-cy={cy}
      disabled={isDisabled}
      className={buttonStyles}
    >
      {renderContent()}
    </button>
  )
}

export default Button
